import { createClient } from '@supabase/supabase-js';

/**
 * Supabase connection.
 *
 * These two values are deliberately hardcoded as defaults rather than required
 * environment variables.
 *
 * The publishable key is *designed* to be public — Supabase issues it precisely
 * so it can ship in a browser bundle, and it is the key every visitor's browser
 * would receive anyway. What actually protects the data is Row Level Security,
 * which is enabled on every table with read-only public policies.
 *
 * Making them defaults removes an entire class of deployment failure: no build
 * variable to forget, no VITE_ prefix to get wrong, no blank page because a
 * dashboard field was typed into the wrong section. An env var still overrides
 * them, so pointing at a branch database or a local stack stays a one-line change.
 *
 * The keys that genuinely must stay secret — OPENAI_API_KEY and the Supabase
 * SECRET key — are never referenced here. They live in Cloudflare's encrypted
 * store and are read only inside worker/index.ts.
 */
const DEFAULT_URL = 'https://pzrnzdvokveugkraeanq.supabase.co';
const DEFAULT_PUBLISHABLE_KEY = 'sb_publishable_FQEjQvg1J6tgv7w8GSi99Q_CIoNjB_k';

/**
 * `import.meta.env` is READ DEFENSIVELY, and that is not paranoia about Vite.
 *
 * Vite replaces `import.meta.env` at build time and vitest provides it, so in
 * both of the places this app actually runs the object is there. It is NOT there
 * under bare `tsx`, which is how every `scripts/*.ts` harness in this project
 * runs — and `scripts/slot-resume.ts` imports `lib/leagueSlot.ts`, which imports
 * this file, so a bare member access here crashed a harness before it printed a
 * line. Nothing about the connection changes: the defaults below are what the
 * browser uses anyway, and a harness that never opens a socket does not care
 * which URL it did not open.
 */
const env = (import.meta as { env?: Record<string, string | undefined> }).env ?? {};

const url = env.VITE_SUPABASE_URL ?? DEFAULT_URL;
const key = env.VITE_SUPABASE_PUBLISHABLE_KEY ?? DEFAULT_PUBLISHABLE_KEY;

export const supabase = createClient(url, key);
